import React, { useContext, useEffect, useMemo, useState } from "react";
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
} from "chart.js";
import { Bar } from "react-chartjs-2";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import brLocale from "date-fns/locale/pt-BR";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { Button, Grid, TextField } from "@material-ui/core";
import Typography from "@material-ui/core/Typography";
import { makeStyles, useTheme } from "@material-ui/core/styles";
import { format } from "date-fns";
import { toast } from "react-toastify";

import api from "../../services/api";
import { i18n } from "../../translate/i18n";
import { AuthContext } from "../../context/Auth/AuthContext";

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

const useStyles = makeStyles((theme) => {
  const isDark = theme.palette.type === "dark" || theme.palette.mode === "dark";

  return {
    root: {
      display: "flex",
      flexDirection: "column",
      gap: theme.spacing(2),
    },
    header: {
      display: "flex",
      justifyContent: "space-between",
      alignItems: "flex-start",
      gap: theme.spacing(1.5),
      flexWrap: "wrap",
    },
    title: {
      fontWeight: 800,
      color: theme.palette.text.primary,
      marginBottom: theme.spacing(0.5),
    },
    subtitle: {
      color: theme.palette.text.secondary,
      fontSize: 13,
      lineHeight: 1.5,
      maxWidth: 520,
    },
    badge: {
      display: "inline-flex",
      alignItems: "center",
      gap: 6,
      padding: "8px 12px",
      borderRadius: 999,
      fontSize: 12,
      fontWeight: 700,
      color: theme.palette.text.primary,
      border: isDark ? "1px solid rgba(148,163,184,0.18)" : "1px solid rgba(15,23,42,0.08)",
      background: isDark ? "rgba(30,41,59,0.92)" : "rgba(15,23,42,0.04)",
    },
    filtersRow: {
      margin: 0,
      width: "100%",
    },
    filterButton: {
      height: 56,
      borderRadius: 14,
      boxShadow: "none",
      textTransform: "none",
      fontWeight: 700,
      minWidth: 120,
    },
    chartWrap: {
      position: "relative",
      minHeight: 320,
    },
    emptyState: {
      minHeight: 320,
      display: "flex",
      alignItems: "center",
      justifyContent: "center",
      textAlign: "center",
      color: theme.palette.text.secondary,
      borderRadius: 18,
      border: isDark ? "1px dashed rgba(148,163,184,0.18)" : "1px dashed rgba(15,23,42,0.12)",
      background: isDark ? "rgba(15,23,42,0.4)" : "rgba(15,23,42,0.02)",
      padding: theme.spacing(3),
    },
  };
});

const buildOptions = (theme) => {
  const isDark = theme.palette.type === "dark" || theme.palette.mode === "dark";

  return {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: "index",
      intersect: false,
    },
    plugins: {
      legend: {
        display: false,
      },
      title: {
        display: false,
      },
      tooltip: {
        backgroundColor: isDark ? "rgba(15,23,42,0.96)" : "rgba(15,23,42,0.9)",
        titleColor: "#fff",
        bodyColor: "#fff",
        padding: 12,
        cornerRadius: 12,
        displayColors: false,
      },
    },
    scales: {
      x: {
        grid: {
          display: false,
        },
        ticks: {
          color: isDark ? "#cbd5e1" : "#475569",
          font: {
            weight: 600,
          },
        },
      },
      y: {
        beginAtZero: true,
        grid: {
          color: isDark ? "rgba(148,163,184,0.14)" : "rgba(15,23,42,0.08)",
          drawBorder: false,
        },
        ticks: {
          precision: 0,
          color: isDark ? "#94a3b8" : "#64748b",
        },
      },
    },
  };
};

export const ChatsUser = () => {
  const classes = useStyles();
  const theme = useTheme();
  const [initialDate, setInitialDate] = useState(new Date());
  const [finalDate, setFinalDate] = useState(new Date());
  const [ticketsData, setTicketsData] = useState({ data: [] });
  const { user } = useContext(AuthContext);

  const companyId = user.companyId;

  useEffect(() => {
    if (companyId) {
      handleGetTicketsInformation();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  const totalTickets = useMemo(
    () =>
      (ticketsData?.data || []).reduce(
        (accumulator, item) => accumulator + Number(item.quantidade || 0),
        0
      ),
    [ticketsData]
  );

  const dataCharts = {
    labels: (ticketsData?.data || []).map((item) => item.nome),
    datasets: [
      {
        data: (ticketsData?.data || []).map((item) => Number(item.quantidade || 0)),
        backgroundColor: [
          "#1d4ed8",
          "#2563eb",
          "#0ea5e9",
          "#06b6d4",
          "#14b8a6",
          "#10b981",
          "#22c55e",
          "#84cc16",
        ],
        borderRadius: 14,
        borderSkipped: false,
        maxBarThickness: 42,
      },
    ],
  };

  const handleGetTicketsInformation = async () => {
    try {
      const { data } = await api.get(
        `/dashboard/ticketsUsers?initialDate=${format(initialDate, "yyyy-MM-dd")}&finalDate=${format(finalDate, "yyyy-MM-dd")}&companyId=${companyId}`
      );
      setTicketsData(data);
    } catch (error) {
      toast.error("Erro ao buscar informações dos tickets");
    }
  };

  const hasData = (ticketsData?.data || []).length > 0;

  return (
    <div className={classes.root}>
      <div className={classes.header}>
        <div>
          <Typography component="h2" variant="h6" className={classes.title}>
            {i18n.t("dashboard.users.totalCallsUser")}
          </Typography>
          <Typography className={classes.subtitle}>
            Compare o volume por atendente com barras mais limpas, melhor contraste e leitura executiva.
          </Typography>
        </div>
        <div className={classes.badge}>{totalTickets} tickets consolidados</div>
      </div>

      <Grid container spacing={2} alignItems="center" className={classes.filtersRow}>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
            <DatePicker
              value={initialDate}
              onChange={(newValue) => setInitialDate(newValue)}
              label={i18n.t("dashboard.date.initialDate")}
              renderInput={(params) => <TextField fullWidth {...params} />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={4}>
          <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
            <DatePicker
              value={finalDate}
              onChange={(newValue) => setFinalDate(newValue)}
              label={i18n.t("dashboard.date.finalDate")}
              renderInput={(params) => <TextField fullWidth {...params} />}
            />
          </LocalizationProvider>
        </Grid>
        <Grid item xs={12} md={4}>
          <Button
            fullWidth
            color="primary"
            variant="contained"
            onClick={handleGetTicketsInformation}
            className={classes.filterButton}
          >
            Filtrar gráfico
          </Button>
        </Grid>
      </Grid>

      {hasData ? (
        <div className={classes.chartWrap}>
          <Bar options={buildOptions(theme)} data={dataCharts} />
        </div>
      ) : (
        <div className={classes.emptyState}>
          Nenhum dado encontrado para o período selecionado.
        </div>
      )}
    </div>
  );
};
